/**
 * LanguageBadge - compact indicator of the language a report / claims report
 * was generated in (pl|en). Shows a globe glyph plus the language code, with an
 * accessible, translated label. Language codes are not country flags on purpose
 * (flags are a known i18n anti-pattern).
 */

import { useTranslation } from "react-i18next";

type ReportLanguage = "pl" | "en";

interface LanguageBadgeProps {
  language: ReportLanguage;
  className?: string;
}

export function LanguageBadge({ language, className = "" }: LanguageBadgeProps) {
  const { t } = useTranslation(["common"]);
  const name = t(`common:language.names.${language}`);
  const code = t(`common:language.code.${language}`);
  const label = t("common:language.reportLanguage", { language: name });

  return (
    <span
      data-test="report-language-badge"
      data-language={language}
      title={label}
      aria-label={label}
      className={`inline-flex items-center gap-1 rounded-full border border-[var(--border)] bg-[var(--secondary)] px-2 py-0.5 text-xs font-medium text-[var(--muted-foreground)] ${className}`}
    >
      <svg
        aria-hidden="true"
        className="h-3 w-3"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <circle cx="12" cy="12" r="9" />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18"
        />
      </svg>
      {code}
    </span>
  );
}
